// ============================================================
// OCCLUSION IN THE WORLD (perf overhaul, Stage 2b) - who stands behind terrain, without projecting a single face.
//
// The svg's test (engine/display/occlusion.ts) asks, per sample of a figure, whether an opaque FACE covers the
// sample's pixel and lies in front of it there: it needs every face projected, sorted and clipped, every camera
// frame - the very work the GPU map stops doing. For convex solids the same question has a world answer: a face
// in front covers the sample exactly when the sample's line of sight to the eye passes through the solid. So each
// sample's sight segment is clipped against each solid's face planes (Cyrus-Beck), after a box test. Under
// perspective the segment stops at the eye's near plane, where the svg's clipping stops what it draws.
//
// The tests hold these answers equal to the svg's on a grid of points under both cameras. Which solids may hide
// is the caller's: the opaque ones for see-through figures, the translucent ones for half-seen.
//
// Pure. Zero React.
// ============================================================
import type { Camera, Viewport } from '../../battlefield/projection';
import { boundingBox, solidGeometry } from '../../battlefield/solids';
import { solidIndex } from '../../battlefield/solidIndex';
import type { Solid, Vec3 } from '../../battlefield/types';
import { nextOccluded } from '../occlusion';
import type { Hider } from '../occlusion';
import { FAR_M, along, lens, plus, segmentMeetsBox } from './sight';
import type { Segment } from './sight';

/** A plane the segment runs along within this is treated as parallel to it. */
const EPS = 1e-9;

/** A sample's line of sight towards the eye, or null when nothing can be in front of it. */
type Sight = (p: Vec3) => Segment | null;

/**
 * The lines of sight under one camera, its eye worked out once: to infinity (FAR_M) under the parallel camera; to
 * the near plane under perspective, as far as the svg would draw what lies on it. Null for a sample past the near
 * plane: the svg does not draw it, so nothing can be in front of it.
 */
function sightFor(camera: Camera, view: Viewport): Sight {
  const { into, eye, depthOf, near } = lens(camera, view);
  if (!eye || !depthOf) return (p) => ({ a: p, b: plus(p, into, -FAR_M) });
  return (p) => {
    const dp = depthOf(p);
    return dp <= near ? null : { a: p, b: along(p, eye, (dp - near) / dp) };
  };
}

/** A solid's planes and box, prepared once per solid object. */
interface Prepared {
  planes: { n: Vec3; d: number }[];
  min: Vec3;
  max: Vec3;
}
const prepared = new WeakMap<Solid, Prepared>();

function prepare(solid: Solid): Prepared {
  const hit = prepared.get(solid);
  if (hit) return hit;
  const g = solidGeometry(solid);
  const planes = g.faces.map((f) => {
    const v = g.vertices[f.indices[0]];
    return { n: f.normal, d: f.normal.x * v.x + f.normal.y * v.y + f.normal.z * v.z };
  });
  const { min, max } = boundingBox(g);
  const made = { planes, min, max };
  prepared.set(solid, made);
  return made;
}

/** The part of a segment inside a solid so far, as parameters along it: [enter, leave]. */
type Span = { enter: number; leave: number };

/**
 * One face plane's cut of the span: a segment heading into the plane's inside raises `enter`, one heading out lowers
 * `leave`; one running along the plane is wholly outside if it starts on the plane or past it. False: no span left.
 */
function cut(span: Span, start: number, rate: number): boolean {
  if (Math.abs(rate) < EPS) return start <= -EPS;
  const t = -start / rate;
  if (rate < 0) span.enter = Math.max(span.enter, t);
  else span.leave = Math.min(span.leave, t);
  return span.leave - span.enter >= 1e-7;
}

/**
 * The part of the segment inside the solid (Cyrus-Beck against its outward face planes), or null when it passes by.
 * Grazing a face or an edge is not passing through, as a face exactly level with the sample is not in front of it on
 * the svg.
 */
function spanThrough(s: Segment, p: Prepared): Span | null {
  const span = { enter: 0, leave: 1 };
  const d = { x: s.b.x - s.a.x, y: s.b.y - s.a.y, z: s.b.z - s.a.z };
  const through = p.planes.every(({ n, d: offset }) =>
    cut(span, n.x * s.a.x + n.y * s.a.y + n.z * s.a.z - offset, n.x * d.x + n.y * d.y + n.z * d.z),
  );
  return through ? span : null;
}

/** Where a segment enters a solid, as a parameter 0-1 along it, or null when it passes by (gpu/pick's question). */
export function entryInto(s: Segment, solid: Solid): number | null {
  const q = prepare(solid);
  if (!segmentMeetsBox(s, q.min, q.max)) return null;
  return spanThrough(s, q)?.enter ?? null;
}

/** The tallest top among the solids, worked out once per list. */
const tops = new WeakMap<readonly Solid[], number>();
function topOf(solids: readonly Solid[]): number {
  let top = tops.get(solids);
  if (top === undefined) {
    top = solids.reduce((m, s) => Math.max(m, prepare(s).max.y), -Infinity);
    tops.set(solids, top);
  }
  return top;
}

/**
 * Where a line of sight stops mattering: where it climbs above the tallest solid (nothing above that can hide
 * anything), else its own end. Null for a sample above every solid with the line rising: nothing can hide it.
 */
function reachOf(s: Segment, top: number): Vec3 | null {
  if (s.a.y >= top && s.b.y >= s.a.y) return null;
  return s.b.y > top && s.b.y > s.a.y ? along(s.a, s.b, (top - s.a.y) / (s.b.y - s.a.y)) : s.b;
}

/** The solids filed under the ground cells from `from` to `to` (battlefield/solidIndex): a superset of the hiders. */
const filedAlong = (solids: readonly Solid[], from: Vec3, to: Vec3) =>
  solidIndex(solids).alongSegment(from.x, from.z, to.x, to.z);

/** Does this solid stand on this line of sight? The box first, then the planes. */
const blocks = (s: Segment, solid: Solid) => entryInto(s, solid) !== null;

const groundDistance = (a: Vec3, b: Vec3) => Math.hypot(b.x - a.x, b.z - a.z);

/**
 * The solids that may hide any of these samples, by one walk of the solid grid instead of asking every one. A
 * figure's samples stand on one vertical (figures `figureSamples`), so every sight line's ground track lies on one
 * ray from the figure's foot, ending where that line stops mattering (`reachOf`, and the near plane under
 * perspective): the walk to the farthest such end passes every cell any of them does. Samples off one vertical
 * ask every solid - a superset, never a miss.
 */
function candidatesFor(samples: readonly Vec3[], solids: readonly Solid[], sight: Sight): readonly Solid[] {
  const [first] = samples;
  if (samples.some((p) => p.x !== first.x || p.z !== first.z)) return solids;
  const top = topOf(solids);
  let farthest: Vec3 | null = null;
  for (const p of samples) {
    const s = sight(p);
    const end = s && reachOf(s, top);
    if (end && (!farthest || groundDistance(first, end) > groundDistance(first, farthest))) farthest = end;
  }
  return farthest ? filedAlong(solids, first, farthest) : [];
}

function hidersBy(p: Vec3, candidates: readonly Solid[], sight: Sight): Set<string> {
  const s = sight(p);
  return new Set(s ? candidates.filter((solid) => blocks(s, solid)).map((solid) => solid.id) : []);
}

function hiddenBy(p: Vec3, candidates: readonly Solid[], sight: Sight): boolean {
  const s = sight(p);
  return !!s && candidates.some((solid) => blocks(s, solid));
}

function fractionBy(samples: readonly Vec3[], solids: readonly Solid[], sight: Sight): number {
  if (!samples.length) return 0;
  const candidates = candidatesFor(samples, solids, sight);
  return samples.filter((p) => hiddenBy(p, candidates, sight)).length / samples.length;
}

/** For each solid, the most of any one figure's samples it hides. */
function sharesBy(figures: readonly (readonly Vec3[])[], solids: readonly Solid[], sight: Sight) {
  const share = new Map<string, number>();
  for (const samples of figures) {
    const counts = new Map<string, number>();
    const candidates = candidatesFor(samples, solids, sight);
    for (const p of samples) for (const id of hidersBy(p, candidates, sight)) counts.set(id, (counts.get(id) ?? 0) + 1);
    for (const [id, n] of counts) share.set(id, Math.max(share.get(id) ?? 0, n / samples.length));
  }
  return share;
}

function xrayBy(
  was: ReadonlySet<string>,
  figures: readonly (readonly Vec3[])[],
  solids: readonly Solid[],
  sight: Sight,
) {
  const share = sharesBy(figures, solids, sight);
  return new Set([...share].filter(([id, f]) => nextOccluded(was.has(id), f)).map(([id]) => id));
}

/** The solids among `solids` that stand between the point and the eye. */
export const worldHiders = (p: Vec3, solids: readonly Solid[], camera: Camera, view: Viewport) => {
  const sight = sightFor(camera, view);
  return hidersBy(p, candidatesFor([p], solids, sight), sight);
};

/** Is the point behind any of these solids, seen from the eye? */
export const worldHidden = (p: Vec3, solids: readonly Solid[], camera: Camera, view: Viewport) => {
  const sight = sightFor(camera, view);
  return hiddenBy(p, candidatesFor([p], solids, sight), sight);
};

/** The share of the samples hidden behind these solids, in [0, 1]: occlusion.ts `hiddenFraction`, in the world. */
export const worldHiddenFraction = (
  samples: readonly Vec3[],
  solids: readonly Solid[],
  camera: Camera,
  view: Viewport,
) => fractionBy(samples, solids, sightFor(camera, view));

/** The x-ray's next wireframes (occlusion.ts `nextXray`), in the world: the same shares, the same hysteresis. */
export const worldXray = (
  was: ReadonlySet<string>,
  figures: readonly (readonly Vec3[])[],
  solids: readonly Solid[],
  camera: Camera,
  view: Viewport,
) => xrayBy(was, figures, solids, sightFor(camera, view));

/** The GPU map's hider: these solids under this camera; null when there are none (nothing can hide anyone). */
export function worldHider(solids: readonly Solid[], camera: Camera, view: Viewport): Hider | null {
  if (!solids.length) return null;
  // The eye worked out once for the frame, not once a sample.
  const sight = sightFor(camera, view);
  return {
    fraction: (samples) => fractionBy(samples, solids, sight),
    xray: (was, figures) => xrayBy(was, figures, solids, sight),
  };
}
